import { Field } from '@chakra-ui/react';
import {
  ContractEndActionEnum,
  IQuoteSettingContractSchema,
  RenewalTermLengthEnum,
} from '@monetize/types/app';
import isNil from 'lodash/isNil';
import toString from 'lodash/toString';
import { Controller, UseFormReturn } from 'react-hook-form';
import {
  MBox,
  MCustomNumberInput,
  MFlex,
  MFormField,
  MRadio,
  MRadioGroup,
  MStack,
  MSwitch,
  MText,
  RadioStyleButtonGroup,
} from '../../../../../components/Monetize';
import { QUOTE_CUSTOM_CONTRACT_LENGTHS } from '../../../../../constants/quotes';
import { getHighlightStyles } from '../../../../../hooks/useSettingsSectionHighlight';
import { SettingsSection } from '../../../components/SettingsSection';
import { QUOTE_SETTING_KEYS } from '../quoteSettingsConfig';

const customContractOptions = QUOTE_CUSTOM_CONTRACT_LENGTHS.map((value) => ({
  label: `${value}`,
  value: `${value}`,
}));
interface ContractRenewalTermsFormProps {
  contractSettingForm: UseFormReturn<IQuoteSettingContractSchema>;
  highlights: Record<string, boolean>;
}
export const ContractRenewalTermsForm = ({
  contractSettingForm,
  highlights,
}: ContractRenewalTermsFormProps) => {
  const {
    setValue,
    watch,
    control,
    clearErrors,
    formState: { errors },
  } = contractSettingForm;

  const renewalTermLengthType = watch(
    'contractRenewalTerms.renewalTermLength.type',
  );
  const watchRenewalLength = watch(
    'contractRenewalTerms.renewalTermLength.months',
  );

  return (
    <>
      <SettingsSection label="Renewal Action">
        <MFormField
          mt="2"
          mb="4"
          error={errors.contractRenewalTerms?.contractEndAction}
        >
          <Controller
            name="contractRenewalTerms.contractEndAction"
            control={control}
            render={({ field: { name, value, onChange, ...rest } }) => (
              <Field.Root orientation="horizontal" gap="4">
                <Field.Label htmlFor={name} fontWeight="400" mb="0" flex="1">
                  Auto Renew Contract
                </Field.Label>

                <MSwitch
                  size="md"
                  isChecked={value === ContractEndActionEnum.RENEW}
                  id={name}
                  aria-describedby={`${name}-subheading`}
                  onChange={(event) =>
                    onChange(
                      event.target.checked
                        ? ContractEndActionEnum.RENEW
                        : ContractEndActionEnum.CANCEL,
                    )
                  }
                />
              </Field.Root>
            )}
          />
        </MFormField>
      </SettingsSection>

      <SettingsSection
        label="Renewal Configuration"
        id={QUOTE_SETTING_KEYS.renewalConfiguration}
        {...getHighlightStyles(
          highlights[QUOTE_SETTING_KEYS.renewalConfiguration],
        )}
      >
        <MBox>
          <MFormField
            mt="2"
            mb="4"
            error={
              errors.contractRenewalTerms?.renewalTermLength?.type ||
              errors.contractRenewalTerms?.renewalTermLength?.months
            }
          >
            <MFlex alignItems="flex-end" gap="2">
              <Controller
                name="contractRenewalTerms.renewalTermLength.type"
                control={control}
                render={({ field: { onChange, ...rest } }) => (
                  <MRadioGroup
                    onChange={(val) => {
                      if (val == null) {
                        return;
                      }

                      onChange(val);

                      if (val === RenewalTermLengthEnum.ContractLength) {
                        setValue(
                          'contractRenewalTerms.renewalTermLength.months',
                          null,
                          {
                            shouldDirty: true,
                            shouldValidate: true,
                          },
                        );
                        if (
                          errors.contractRenewalTerms?.renewalTermLength?.months
                        ) {
                          clearErrors(
                            'contractRenewalTerms.renewalTermLength.months',
                          );
                        }
                      }
                    }}
                    {...rest}
                  >
                    <MStack>
                      <MRadio
                        isInvalid={
                          !!errors.contractRenewalTerms?.renewalTermLength?.type
                        }
                        value={RenewalTermLengthEnum.ContractLength}
                      >
                        Same as Contract Length
                      </MRadio>
                      <MRadio
                        value={RenewalTermLengthEnum.FixedMonths}
                        minH={8}
                      >
                        Custom Length in Months
                      </MRadio>
                    </MStack>
                  </MRadioGroup>
                )}
              />
            </MFlex>

            {renewalTermLengthType === RenewalTermLengthEnum.FixedMonths && (
              <MBox pl="5">
                <Controller
                  control={control}
                  name="contractRenewalTerms.renewalTermLength.months"
                  render={({ field: { value, onChange, ...rest } }) => (
                    <MCustomNumberInput
                      w="10rem"
                      inputMode="numeric"
                      value={value ?? ''}
                      onChange={(
                        valueAsString: string,
                        valueAsNumber: number,
                      ) => {
                        if (valueAsString === '') {
                          onChange(null);
                          return;
                        }
                        onChange(valueAsNumber);
                      }}
                      {...rest}
                    />
                  )}
                />

                <RadioStyleButtonGroup
                  name="renewalLength"
                  containerProps={{ maxW: '160px', mt: 2 }}
                  options={customContractOptions}
                  value={`${watchRenewalLength || ''}` || undefined}
                  defaultValue={`${watchRenewalLength || ''}` || undefined}
                  onChange={(value: string) => {
                    setValue(
                      'contractRenewalTerms.renewalTermLength.months',
                      Number(value),
                      {
                        shouldDirty: true,
                        shouldValidate: true,
                      },
                    );
                  }}
                />
              </MBox>
            )}
          </MFormField>

          <MFormField
            mt="4"
            label="Notice Period (in days)"
            isRequired
            labelProps={{
              display: 'block',
            }}
            tooltip="Notice of non-renewal by this number of days before Contract ends"
            error={errors.contractRenewalTerms?.autoRenewalNoticePeriod}
          >
            <Controller
              control={control}
              name="contractRenewalTerms.autoRenewalNoticePeriod"
              render={({ field: { value, onChange, ...field } }) => (
                <MCustomNumberInput
                  maxW="11.25rem"
                  value={!isNil(value) ? toString(value) : ''}
                  onChange={(valueAsString: string, valueAsNumber: number) => {
                    if (valueAsString === '') {
                      onChange('');
                      return;
                    }
                    onChange(valueAsNumber);
                  }}
                  {...field}
                />
              )}
            />
          </MFormField>
          <MText mt="2" fontWeight="400" color="tGray.acGray">
            Example: If Contract with auto-renewal ends on July 15th with notice
            period of 10 days, notice of non-renewal must be received by July
            5th
          </MText>
        </MBox>
      </SettingsSection>
    </>
  );
};
